import React, { useEffect, useState, useCallback } from "react";
import { Maximize, Minimize, ExternalLink, X, Share } from "lucide-react";

// ---- low-level fullscreen helpers (cross-browser) --------------------------
const docEl = () => document.documentElement;
const fsElement = () =>
  document.fullscreenElement || document.webkitFullscreenElement || null;
const fsSupported = () =>
  !!(
    docEl().requestFullscreen ||
    docEl().webkitRequestFullscreen ||
    document.fullscreenEnabled ||
    document.webkitFullscreenEnabled
  );
// Releasing any implicit orientation lock is CRITICAL: on Android Chrome,
// entering the Fullscreen API can pin the screen to the orientation it was in
// when fullscreen started. If that happens in portrait, the device stops
// rotating to landscape and gameplay becomes unreachable. `unlock()` keeps the
// device free to rotate inside fullscreen (no-op where unsupported).
function unlockOrientation() {
  try {
    if (window.screen && window.screen.orientation && window.screen.orientation.unlock) {
      window.screen.orientation.unlock();
    }
  } catch (e) { /* not supported / not allowed — ignore */ }
}
async function enterFs() {
  const el = docEl();
  const fn = el.requestFullscreen || el.webkitRequestFullscreen;
  if (fn) {
    try {
      await fn.call(el, { navigationUI: "hide" });
    } catch (e) {
      try { await fn.call(el); } catch (e2) { /* blocked (in-app browser) */ }
    }
    unlockOrientation();
  }
}
async function exitFs() {
  const fn = document.exitFullscreen || document.webkitExitFullscreen;
  if (fn) {
    try { await fn.call(document); } catch (e) { /* no-op */ }
  }
}

// ---- environment detection -------------------------------------------------
function detectMobile() {
  try {
    const coarse = window.matchMedia && window.matchMedia("(pointer: coarse)").matches;
    const sw = window.screen?.width || 0;
    const sh = window.screen?.height || 0;
    const shortSide = Math.min(sw, sh) || Math.min(window.innerWidth, window.innerHeight);
    return !!coarse && shortSide < 900;
  } catch (e) {
    return false;
  }
}
function detectStandalone() {
  try {
    return (
      (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
      window.navigator.standalone === true
    );
  } catch (e) {
    return false;
  }
}
// In-app webviews (links opened inside WhatsApp, Instagram, Facebook, etc.)
// render inside a Custom Tab where the Fullscreen API is blocked — the only
// real fix there is to open the page in the real browser.
function detectInAppBrowser() {
  try {
    const ua = navigator.userAgent || "";
    const rules = [
      "FBAN", "FBAV", "FB_IAB", "Instagram", "Line/", "WhatsApp",
      "Snapchat", "Twitter", "TikTok", "Pinterest", "LinkedInApp",
      "MicroMessenger", "GSA/",
    ];
    const isWebView = /; wv\)/.test(ua) || /\bwv\b/.test(ua);
    return isWebView || rules.some((r) => ua.indexOf(r) !== -1);
  } catch (e) {
    return false;
  }
}
// iPhone/iPod Safari has NO Fullscreen API on the page, so the toolbar can't
// be hidden — the only way to run without browser bars is "Add to Home
// Screen". (Newer iPadOS Safari is desktop-class and DOES support it.)
function detectIOS() {
  try {
    const ua = navigator.userAgent || "";
    const iOSUA = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    // iPadOS 13+ reports as "Macintosh" but is a touch device.
    const iPadOS = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
    return iOSUA || iPadOS;
  } catch (e) {
    return false;
  }
}

/**
 * MobileFullscreen — on mobile the browser's top toolbar (address bar / share
 * bar) steals vertical space and breaks the landscape gameplay layout. We:
 *   1. request fullscreen on the user's FIRST tap (once per session), which
 *      hides the toolbar in browsers that support the Fullscreen API;
 *   2. expose a manual fullscreen toggle button;
 *   3. show a banner suggesting "open in Chrome/Safari" when running inside an
 *      in-app webview where fullscreen is blocked.
 *
 * Mounted at the App root (OUTSIDE the transformed gameplay-root) so its
 * position:fixed elements stay anchored to the layout viewport.
 */
export default function MobileFullscreen() {
  const [isMobile] = useState(detectMobile);
  const [isStandalone] = useState(detectStandalone);
  const [isInApp] = useState(detectInAppBrowser);
  const [isIOS] = useState(detectIOS);
  const [supported] = useState(fsSupported);
  const [isFs, setIsFs] = useState(!!fsElement());
  const [bannerDismissed, setBannerDismissed] = useState(
    () => sessionStorage.getItem("inapp_banner_dismissed") === "1"
  );
  const [iosHintDismissed, setIosHintDismissed] = useState(
    () => sessionStorage.getItem("ios_fs_hint_dismissed") === "1"
  );
  const [landscape, setLandscape] = useState(
    () => (window.innerWidth || 0) > (window.innerHeight || 0)
  );
  const [copied, setCopied] = useState(false);

  const active = isMobile && !isStandalone;

  // Track orientation so the iOS hint only shows in landscape (gameplay),
  // where the PWA install banner is hidden — avoids duplicate prompts.
  useEffect(() => {
    if (!active) return;
    const update = () => setLandscape((window.innerWidth || 0) > (window.innerHeight || 0));
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    if (window.visualViewport) window.visualViewport.addEventListener("resize", update);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
      if (window.visualViewport) window.visualViewport.removeEventListener("resize", update);
    };
  }, [active]);

  // Track fullscreen state changes.
  useEffect(() => {
    if (!active) return;
    const onChange = () => setIsFs(!!fsElement());
    document.addEventListener("fullscreenchange", onChange);
    document.addEventListener("webkitfullscreenchange", onChange);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      document.removeEventListener("webkitfullscreenchange", onChange);
    };
  }, [active]);

  // Auto-enter fullscreen on the FIRST user gesture (once per session).
  useEffect(() => {
    if (!active || !supported || isInApp) return;
    if (sessionStorage.getItem("fs_auto_done") === "1") return;
    const handler = () => {
      // Only auto-enter fullscreen when the device is ALREADY in landscape.
      // Entering fullscreen in portrait can lock the device to portrait on
      // Android, which then prevents rotating to landscape to play. Waiting
      // for landscape guarantees we never pin the wrong orientation.
      const isLandscape = (window.innerWidth || 0) > (window.innerHeight || 0);
      if (!isLandscape) return; // keep listening until the user is in landscape
      sessionStorage.setItem("fs_auto_done", "1");
      cleanup();
      if (!fsElement()) enterFs();
    };
    const cleanup = () => {
      window.removeEventListener("pointerdown", handler, true);
      window.removeEventListener("touchend", handler, true);
      window.removeEventListener("click", handler, true);
    };
    window.addEventListener("pointerdown", handler, true);
    window.addEventListener("touchend", handler, true);
    window.addEventListener("click", handler, true);
    return cleanup;
  }, [active, supported, isInApp]);

  const toggle = useCallback(() => {
    if (fsElement()) exitFs();
    else enterFs();
  }, []);

  const dismissBanner = useCallback(() => {
    sessionStorage.setItem("inapp_banner_dismissed", "1");
    setBannerDismissed(true);
  }, []);

  const dismissIosHint = useCallback(() => {
    sessionStorage.setItem("ios_fs_hint_dismissed", "1");
    setIosHintDismissed(true);
  }, []);

  const copyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) { /* clipboard blocked */ }
  }, []);

  if (!active) return null;

  const showBanner = isInApp && !bannerDismissed;
  // iOS without Fullscreen API (iPhone Safari): can't hide the browser bars.
  // Suggest "Add to Home Screen". Only in landscape (gameplay) where the PWA
  // install banner is hidden, so we never show two prompts at once.
  const showIosHint =
    isIOS && !supported && !isInApp && !iosHintDismissed && landscape;

  return (
    <>
      {/* In-app browser warning — fullscreen is blocked here. */}
      {showBanner && (
        <div
          data-testid="inapp-browser-banner"
          className="fixed top-0 inset-x-0 z-[10050] bg-slate-900/95 text-white px-3 py-2 flex items-center gap-2 shadow-lg backdrop-blur-sm"
          style={{ paddingTop: "max(0.5rem, env(safe-area-inset-top))" }}
        >
          <ExternalLink className="w-4 h-4 shrink-0 text-amber-300" />
          <span className="text-xs leading-tight flex-1">
            Para a melhor experiência, abre esta página no <strong>Chrome</strong> ou <strong>Safari</strong> (menu <strong>⋮</strong> → “Abrir no navegador”).
          </span>
          <button
            type="button"
            onClick={copyLink}
            data-testid="inapp-copy-link"
            className="text-[11px] font-bold bg-white/15 hover:bg-white/25 rounded-md px-2 py-1 whitespace-nowrap"
          >
            {copied ? "Copiado!" : "Copiar link"}
          </button>
          <button
            type="button"
            onClick={dismissBanner}
            data-testid="inapp-banner-dismiss"
            aria-label="Fechar aviso"
            className="p-1 rounded-md hover:bg-white/15"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* iOS (no Fullscreen API) — suggest Add to Home Screen to drop the bars. */}
      {showIosHint && (
        <div
          data-testid="ios-fullscreen-hint"
          className="fixed top-0 inset-x-0 z-[10050] bg-slate-900/95 text-white px-3 py-2 flex items-center gap-2 shadow-lg backdrop-blur-sm"
          style={{ paddingTop: "max(0.5rem, env(safe-area-inset-top))" }}
        >
          <Share className="w-4 h-4 shrink-0 text-sky-300" />
          <span className="text-xs leading-tight flex-1">
            No iPhone, para jogar sem as barras do navegador: toca em <strong>Partilhar</strong> <span className="inline-flex align-middle"><Share className="w-3 h-3" /></span> → <strong>“Adicionar ao Ecrã Principal”</strong>.
          </span>
          <button
            type="button"
            onClick={dismissIosHint}
            data-testid="ios-hint-dismiss"
            aria-label="Fechar dica"
            className="p-1 rounded-md hover:bg-white/15"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Manual fullscreen toggle (hidden when API unsupported, e.g. iOS Safari). */}
      {supported && !isInApp && (
        <button
          type="button"
          onClick={toggle}
          data-testid="fullscreen-toggle"
          aria-label={isFs ? "Sair de ecrã inteiro" : "Ecrã inteiro"}
          className="fixed top-2 left-2 z-[10040] grid place-items-center w-9 h-9 rounded-lg bg-slate-900/45 hover:bg-slate-900/70 text-white backdrop-blur-sm shadow-md ring-1 ring-white/20 transition-colors"
          style={{
            top: "max(0.5rem, env(safe-area-inset-top))",
            left: "max(0.5rem, env(safe-area-inset-left))",
          }}
        >
          {isFs ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />}
        </button>
      )}
    </>
  );
}
